import axios from 'axios';
import io from 'socket.io-client';
import { message, Modal, notification } from 'antd';
import { action, observable, runInAction } from 'mobx';
const confirm = Modal.confirm;

export class OdysseyBotStore {
    @observable orderbook = [];
    @observable trades = [];
    @observable config = null;
    @observable position = null;
    @observable positionDetails = null;
    @observable instrument = null;
    @observable longStrategy = null;
    @observable shortStrategy = null;
    @observable taStrategy = null;
    @observable activeTradingSession = null;
    @observable completeTradingSessions = null;
    @observable activeTradingSessionTrades = [];
    @observable tradingSession = null;
    @observable tradingSessionPositions = [];
    @observable top10Leaderboard = [];
    @observable logs = [];

    constructor({ rootStore }) {
        this.rootStore = rootStore;
    }

    @action connectToStream(id) {
        this.botId = this.config._id;
        this.socket = io(
            this.config.env === 'local'
                ? `//${this.config.location}`
                : `https://${this.config.exchange}.apextrader.io/`,
            {
                path: this.config.env === 'local' ? '' : `/odyssey-${this.config._id}/socket.io/`,
                query: `token=${this.rootStore.userStore.idToken}`
            }
        );

        this.socket.on('connect', () => {
            console.log('connected');
        });

        this.socket.on('disconnect', () => {
            console.log('disconnected');
        });

        this.socket.on('orderbook', ({ data }) => {
            runInAction(() => {
                this.orderbook = data;
            });
        });

        this.socket.on('trades', ({ data }) => {
            runInAction(() => {
                this.trades = [...data, ...this.trades];
            });
        });

        this.socket.on('position', ({ data }) => {
            runInAction(() => {
                this.position = data;
            });
        });

        this.socket.on('position:details', ({ data }) => {
            runInAction(() => {
                this.positionDetails = data;
            });
        });

        this.socket.on('instrument', ({ data }) => {
            runInAction(() => {
                this.instrument = { ...data };
            });
        });

        this.socket.on('strategy:long', ({ data }) => {
            runInAction(() => {
                this.longStrategy = data;
            });
        });

        this.socket.on('strategy:short', ({ data }) => {
            runInAction(() => {
                this.shortStrategy = data;
            });
        });

        this.socket.on('strategy:ta', ({ data }) => {
            runInAction(() => {
                this.taStrategy = data;
            });
        });

        this.socket.on('tradingsession', ({ data }) => {
            runInAction(() => {
                this.activeTradingSession = data;
            });
        });

        this.socket.on('tradingsession::last_trades', ({ data }) => {
            runInAction(() => {
                this.activeTradingSessionTrades = data;
            });
        });

        this.socket.on('notification', ({ data }) => {
            console.log(data);
            this.handleNotifications(data);
        });
    }

    async reboot() {
        await axios.get(`${this.rootStore.env.botApi}odyssey/${this.config._id}/reboot`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
        notification.success({
            message: 'Bot rebooting',
            description: 'This may take a few minutes.'
        });
    }

    handleNotifications({ title, body }) {
        console.log('notification', title, body);
        if (!('Notification' in window)) {
            alert('This browser does not support desktop notification');
        }

        // Let's check whether notification permissions have already been granted
        else if (Notification.permission === 'granted') {
            // If it's okay let's create a notification
            new Notification(title, {
                body,
                icon: 'https://console.apextrader.io/android-chrome-192x192.png'
            });
        }

        // Otherwise, we need to ask the user for permission
        else if (Notification.permission !== 'denied') {
            Notification.requestPermission().then(function(permission) {
                // If the user accepts, let's create a notification
                if (permission === 'granted') {
                    new Notification(title, {
                        body,
                        icon: 'https://console.apextrader.io/android-chrome-192x192.png'
                    });
                }
            });
        }
    }
    get connectionURI() {
        return this.config.env === 'local'
            ? `//${this.config.location}`
            : `https://${this.config.exchange}.apextrader.io/odyssey-${this.config._id}`;
    }

    async proxy(endpoint) {
        const res = await axios.get(`${this.connectionURI}/${endpoint}/`);
        return res.data;
    }

    async getConfig(id) {
        const res = await axios.get(`${this.rootStore.env.botApi}${id}`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
        runInAction(() => {
            this.config = res.data;
        });
    }

    async updateConfig(data) {
        try {
            if (!data.api_key) delete data.api_key;
            if (!data.api_secret) delete data.api_secret;

            const res = await axios.post(`${this.rootStore.env.botApi}${this.botId}`, data, {
                headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
            });
            runInAction(() => {
                this.config = res.data;
                message.success('Configuration updated');
            });
        } catch (e) {
            message.error(`There was an error updating: ${e.message}`);
        }
    }

    async getActiveTradingSession(id) {
        const res = await axios.get(`${this.rootStore.env.botApi}${id}/trading_session/active`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
        runInAction(() => {
            this.activeTradingSession = res.data;
        });
    }

    async getTradingSession(botId, sessionId) {
        const res = await axios.get(
            `${this.rootStore.env.botApi}${botId}/trading_session/${sessionId}`,
            {
                headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
            }
        );
        runInAction(() => {
            this.tradingSession = res.data;
        });
    }

    async getTradingSessionPositions(botId, sessionId) {
        const res = await axios.get(
            `${this.rootStore.env.botApi}${botId}/trading_session/${sessionId}/positions`,
            {
                headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
            }
        );
        runInAction(() => {
            this.tradingSessionPositions = res.data;
        });
    }

    async getCompleteTradingSessions(id) {
        const res = await axios.get(`${this.rootStore.env.botApi}${id}/trading_session/complete`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
        runInAction(() => {
            this.completeTradingSessions = res.data;
        });
    }

    async setActiveTradingSessionState(status) {
        let session = { ...this.activeTradingSession };
        session.status = status;
        const res = await axios.put(
            `${this.rootStore.env.botApi}${session.bot_id}/trading_session/${session._id}`,
            session,
            {
                headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
            }
        );
        runInAction(() => {
            if (status === 'COMPLETE') {
                this.activeTradingSession = null;
            } else {
                this.activeTradingSession = res.data;
            }
        });
    }

    async getLogs(id) {
        const res = await axios.get(`${this.rootStore.env.botApi}odyssey/${id}/logs`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });

        runInAction(() => {
            this.logs = res.data.logs.split('\n').reverse();
        });
    }

    async getTop10Leaderboard(pair = 'BTCUSD') {
        const res = await axios.get(`${this.rootStore.env.leaderboardApi}odyssey/top/${[pair]}`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });

        runInAction(() => {
            this.top10Leaderboard = res.data.map((entry, i) => {
                entry.place = i + 1;
                return entry;
            });
        });
    }

    async createTradingSession(id, data) {
        const res = await axios.post(`${this.rootStore.env.botApi}${id}/trading_session`, data, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });
        runInAction(() => {
            this.activeTradingSession = res.data;
        });
    }

    async deleteTradingSessions(botId, sessionId) {
        await axios.delete(`${this.rootStore.env.botApi}${botId}/trading_session/${sessionId}`, {
            headers: { Authorization: `Bearer ${this.rootStore.userStore.idToken}` }
        });

        await this.getCompleteTradingSessions();
    }

    deleteCurrentPosition() {
        confirm({
            title: 'Are you sure ?',
            content: 'Ensure you have closed this position on the exchange',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: () => {
                this.socket.emit('message', { channel: 'position:delete' });
            },
            onCancel() {
                //noop
            }
        });
    }

    async getKline(symbol, resolution, from, to) {
        return this.proxy(`v1/kline/${symbol}/${resolution}?from=${from}&to=${to}`);
    }

    streamKline(symbol, resolution) {
        this.socket.emit('message', { channel: 'candles:stream', symbol, resolution });
    }

    updateTakeProfitPrice(price) {
        this.socket.emit('message', { channel: 'position:update-tp-price', price });
    }

    updateStopLossPrice(price) {
        this.socket.emit('message', { channel: 'position:update-sl-price', price });
    }
}



// WEBPACK FOOTER //
// ./src/stores/OdysseyBot/index.js